/**
 * WebGPU Detection and Support Utilities
 * 
 * WebGPU is a modern graphics API that can provide better performance than WebGL.
 * Browser support: Chrome 113+, Edge 113+, Safari 18+, Firefox (experimental)
 */

// Tipos derivados de navigator.gpu (@webgpu/types): o ESLint não conhece os globais GPU*
type WebGPU = typeof navigator.gpu;
type GPUAdapterT = NonNullable<Awaited<ReturnType<WebGPU["requestAdapter"]>>>;
type GPUAdapterInfoT = GPUAdapterT["info"];
type GPUTextureFormatT = ReturnType<WebGPU["getPreferredCanvasFormat"]>;

export interface WebGPUCapabilities {
  supported: boolean;
  adapter: GPUAdapterT | null;
  adapterInfo: GPUAdapterInfoT | null;
  features: string[];
  limits: Record<string, number>;
  preferredFormat: GPUTextureFormatT | null;
}

let cachedCapabilities: WebGPUCapabilities | null = null;

/**
 * Check if WebGPU is supported in the current browser
 */
export function isWebGPUSupported(): boolean {
  return typeof navigator !== 'undefined' && 'gpu' in navigator;
}

/**
 * Get detailed WebGPU capabilities
 * Results are cached for performance
 */
export async function getWebGPUCapabilities(): Promise<WebGPUCapabilities> {
  if (cachedCapabilities) {
    return cachedCapabilities;
  }

  if (!isWebGPUSupported()) {
    cachedCapabilities = {
      supported: false,
      adapter: null,
      adapterInfo: null,
      features: [],
      limits: {},
      preferredFormat: null,
    };
    return cachedCapabilities;
  }

  try {
    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: 'high-performance',
    });

    if (!adapter) {
      cachedCapabilities = {
        supported: false,
        adapter: null,
        adapterInfo: null,
        features: [],
        limits: {},
        preferredFormat: null,
      };
      return cachedCapabilities;
    }

    // Use adapter.info property (requestAdapterInfo is deprecated)
    const adapterInfo = adapter.info;
    const features = Array.from(adapter.features);
    
    const limits: Record<string, number> = {};
    const limitKeys = [
      'maxTextureDimension1D',
      'maxTextureDimension2D',
      'maxTextureDimension3D',
      'maxTextureArrayLayers',
      'maxBindGroups',
      'maxDynamicUniformBuffersPerPipelineLayout',
      'maxDynamicStorageBuffersPerPipelineLayout',
      'maxSampledTexturesPerShaderStage',
      'maxSamplersPerShaderStage',
      'maxStorageBuffersPerShaderStage',
      'maxStorageTexturesPerShaderStage',
      'maxUniformBuffersPerShaderStage',
      'maxUniformBufferBindingSize',
      'maxStorageBufferBindingSize',
      'maxVertexBuffers',
      'maxVertexAttributes',
      'maxVertexBufferArrayStride',
      'maxComputeWorkgroupStorageSize',
      'maxComputeInvocationsPerWorkgroup',
      'maxComputeWorkgroupSizeX',
      'maxComputeWorkgroupSizeY',
      'maxComputeWorkgroupSizeZ',
      'maxComputeWorkgroupsPerDimension',
    ];

    for (const key of limitKeys) {
      const value = (adapter.limits as unknown as Record<string, unknown>)[key];
      if (typeof value === 'number') {
        limits[key] = value;
      }
    }

    const preferredFormat = navigator.gpu.getPreferredCanvasFormat();

    cachedCapabilities = {
      supported: true,
      adapter,
      adapterInfo,
      features,
      limits,
      preferredFormat,
    };

    console.log('[WebGPU] Capabilities detected:', {
      vendor: adapterInfo.vendor,
      architecture: adapterInfo.architecture,
      device: adapterInfo.device,
      description: adapterInfo.description,
      features: features.length,
      preferredFormat,
    });

    return cachedCapabilities;
  } catch (error) {
    console.warn('[WebGPU] Failed to get capabilities:', error);
    cachedCapabilities = {
      supported: false,
      adapter: null,
      adapterInfo: null,
      features: [],
      limits: {},
      preferredFormat: null,
    };
    return cachedCapabilities;
  }
}

/**
 * Get a user-friendly description of the GPU
 */
export function getGPUDescription(info: GPUAdapterInfoT | null): string {
  if (!info) return 'Unknown GPU';
  
  const parts = [];
  if (info.vendor) parts.push(info.vendor);
  if (info.device) parts.push(info.device);
  if (info.architecture) parts.push(`(${info.architecture})`);
  
  return parts.length > 0 ? parts.join(' ') : info.description || 'Unknown GPU';
}

/**
 * Check if the GPU has specific features
 */
export function hasWebGPUFeature(feature: string): boolean {
  return cachedCapabilities?.features.includes(feature) ?? false;
}

/**
 * Get render backend preference (for settings UI)
 */
export type RenderBackend = 'webgl' | 'webgpu' | 'auto';

export function getRenderBackendInfo(): {
  current: RenderBackend;
  webglSupported: boolean;
  webgpuSupported: boolean;
  recommended: RenderBackend;
} {
  const webglSupported = typeof WebGLRenderingContext !== 'undefined';
  const webgpuSupported = isWebGPUSupported();
  
  return {
    current: 'webgl', // Currently we use WebGL
    webglSupported,
    webgpuSupported,
    recommended: webgpuSupported ? 'webgpu' : 'webgl',
  };
}
